/**
 * reportConstants.js
 * Static data: report definitions, field configs, formatting helpers.
 * Extracted from ReportsPage.js — Stage 2 logic refactor.
 */
import { BarChart3, DollarSign, TrendingUp, AlertCircle, Calendar, Settings2 } from 'lucide-react';

export const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v)
    : '-';

export const GREEN_DOOR_LOGO =
  'https://customer-assets.emergentagent.com/job_thoroughbred-deals/artifacts/xmrjz10b_Powered%20By%20Green%20Door.png';

export const PREFS_KEY = 'report_fields_v6_';

export const REPORTS = [
  { id: 'daily-sale-updates',      label: 'Daily Sale Updates',      icon: Calendar,    desc: 'Sale activity per horse — views, vetting, notes' },
  { id: 'tenant-financials',       label: 'Consignor Financials',     icon: DollarSign,  desc: 'Commissions summary by Account (All-Time & YTD)' },
  { id: 'net-proceeds',            label: 'Net Proceeds',             icon: TrendingUp,  desc: 'Owner proceeds after commissions, expenses, and fees' },
  { id: 'sales-performance',       label: 'Sales Performance',        icon: BarChart3,   desc: 'ROI by sire, dam, crop year, or sale' },
  { id: 'outstanding-receivables', label: 'Outstanding Receivables',  icon: AlertCircle, desc: 'Unpaid invoices with aging buckets' },
  { id: 'custom',                  label: 'Custom Report',            icon: Settings2,   desc: 'Build your own custom report' },
];

// All available fields per report, grouped by entity
export const REPORT_FIELDS = {
  'daily-sale-updates': {
    'Horse Details': [
      { key: 'horse_age',            label: 'Age',            default: true },
      { key: 'horse_gender',         label: 'Gender',         default: true },
      { key: 'sold_as',              label: 'Sold As',        default: true },
      { key: 'horse_sire',           label: 'Sire' },
      { key: 'horse_dam',            label: 'Dam' },
      { key: 'horse_color',          label: 'Color' },
      { key: 'horse_cover_status',   label: 'Cover Status' },
      { key: 'horse_covering_sire',  label: 'Covering Sire' },
      { key: 'horse_vices',          label: 'Vices' },
    ],
    'Card / Views Columns': [
      { key: 'col_horses_on_card',      label: 'Horses on Card',    default: true },
      { key: 'col_view_notes',          label: 'View Notes',        default: true },
      { key: 'col_key_associations',    label: 'Key Associations who Viewed or Vetted',  default: true },
    ],
  },
  'tenant-financials': {
    'Account': [
      { key: 'account_name',    label: 'Account',          default: true },
      { key: 'rating',          label: 'Rating',           default: true },
      { key: 'industry_role',   label: 'Industry Role' },
      { key: 'account_manager', label: 'Account Manager' },
    ],
    'Financials': [
      { key: 'all_time_commissions', label: 'All-Time Commissions', default: true },
      { key: 'ytd_commissions',      label: 'YTD Commissions',      default: true },
      { key: 'all_time_revenue',     label: 'All-Time Gross Sales' },
      { key: 'ytd_revenue',          label: 'YTD Gross Sales' },
      { key: 'invoice_count',        label: 'Invoice Count',        default: true },
    ],
  },
  'net-proceeds': {
    'Sale': [
      { key: 'horse_name',  label: 'Horse',        default: true },
      { key: 'sale_name',   label: 'Sale',         default: true },
      { key: 'sale_company',label: 'Sale Company' },
      { key: 'owners',      label: 'Owners',       default: true },
      { key: 'hip_number',  label: 'Hip #',        default: true },
    ],
    'Proceeds': [
      { key: 'sale_price',             label: 'Sale Price',            default: true },
      { key: 'sale_company_commission',label: 'Sale Co. Commission' },
      { key: 'entry_fee',              label: 'Entry Fee' },
      { key: 'other_fees',             label: 'Other Fees' },
      { key: 'total_fees',             label: 'Total Fees',            default: true },
      { key: 'net_proceeds',           label: 'Net Proceeds',          default: true },
      { key: 'commission',             label: 'Consignor Commission',  default: true },
      { key: 'owner_net_proceeds',     label: "Owner's Net Proceeds",  default: true },
    ],
    'Buyer': [
      { key: 'signed_ticket_as', label: 'Signed Ticket As' },
      { key: 'payment_method',   label: 'Payment Method' },
    ],
  },
  'sales-performance': {
    'Performance': [
      { key: 'group',         label: 'Group',         default: true },
      { key: 'sold_count',    label: 'Sold',          default: true },
      { key: 'rna_count',     label: 'RNA',           default: true },
      { key: 'total_entries', label: 'Total Entries' },
      { key: 'sold_rate',     label: 'Sold Rate %',   default: true },
      { key: 'avg_price',     label: 'Avg Price',     default: true },
      { key: 'median_price',  label: 'Median Price' },
      { key: 'total_revenue', label: 'Gross Sales',   default: true },
      { key: 'top_horse',     label: 'Top Horse',     default: true },
    ],
  },
  'outstanding-receivables': {
    'Invoice': [
      { key: 'account_name',   label: 'Account',       default: true },
      { key: 'invoice_number', label: 'Invoice #',     default: true },
      { key: 'invoice_date',   label: 'Invoice Date' },
      { key: 'due_date',       label: 'Due Date',      default: true },
      { key: 'amount',         label: 'Amount',        default: true },
    ],
    'Aging': [
      { key: 'days_overdue', label: 'Days Overdue',  default: true },
      { key: 'bucket',       label: 'Aging Bucket',  default: true },
    ],
  },
};
